import { hirdetesiLink } from '@ertekbecslo/kozos/meres';
import { FOGLALT_SLUGOK, halozatKulcs, munkaidoTisztitas, type HalozatKulcs, type Munkaido } from '@ertekbecslo/kozos/iroda';
import { budapestiMaiNap, budapestiNapEleje } from '../elemzes/idoszak.ts';
import type { Database } from '../lib/adatbazis.ts';

export type OfficesInsert = Database['public']['Tables']['offices']['Insert'];
type OfficesRow = Database['public']['Tables']['offices']['Row'];

export interface IrodaUrlapAdat {
  slug: string;
  nev: string;
  halozat: string;
  halozatKulcs: HalozatKulcs | null;
  aktiv: boolean;
  regio: string;
  varos: string;
  logoUrl: string;
  kapcsolatEmail: string;
  kapcsolatTelefon: string;
  hivasTelefon: string;
  munkaido: Munkaido;
  slaPerc: number;
  /** A partner-ÁSZF 1.1 (minőségellenőrzés) kifejezett elfogadásának napja, `ÉÉÉÉ-HH-NN` (budapesti nap); üres: nincs elfogadva. */
  minosegElfogadva: string;
  /** Az új lead kiosztása: körforgás az értékesítők között, vagy minden lead a kijelölt irodavezetőhöz. */
  kiosztasMod: 'automatikus' | 'vezetohoz';
  /** A kijelölt irodavezető (user_id); csak `vezetohoz` módban kötelező. */
  kiosztasVezetoId: string | null;
}

export function irodaUrlapUres(): IrodaUrlapAdat {
  return {
    slug: '', nev: '', halozat: '', halozatKulcs: null, aktiv: true, regio: '', varos: '', logoUrl: '',
    kapcsolatEmail: '', kapcsolatTelefon: '', hivasTelefon: '', munkaido: {}, slaPerc: 120, minosegElfogadva: '',
    kiosztasMod: 'automatikus', kiosztasVezetoId: null,
  };
}

function jsonSzoveg(j: unknown, kulcs: string): string {
  if (j && typeof j === 'object' && !Array.isArray(j)) {
    const v = (j as Record<string, unknown>)[kulcs];
    if (typeof v === 'string') return v;
  }
  return '';
}

export function irodaUrlapSorbol(sor: OfficesRow): IrodaUrlapAdat {
  return {
    slug: sor.slug,
    nev: sor.nev,
    halozat: sor.halozat ?? '',
    halozatKulcs: halozatKulcs(sor.halozat_kulcs),
    aktiv: sor.aktiv,
    regio: sor.regio ?? '',
    varos: sor.varos ?? '',
    logoUrl: sor.logo_url ?? '',
    kapcsolatEmail: jsonSzoveg(sor.kapcsolat, 'email'),
    kapcsolatTelefon: jsonSzoveg(sor.kapcsolat, 'telefon'),
    hivasTelefon: sor.hivas_telefon ?? '',
    munkaido: munkaidoTisztitas(sor.munkaido),
    slaPerc: sor.sla_perc,
    minosegElfogadva: sor.minoseg_elfogadva_at ? budapestiMaiNap(new Date(sor.minoseg_elfogadva_at)) : '',
    kiosztasMod: sor.kiosztas_mod === 'vezetohoz' ? 'vezetohoz' : 'automatikus',
    kiosztasVezetoId: sor.kiosztas_vezeto_id ?? null,
  };
}

const szokoz = (s: string) => s.replace(/\s+/g, '');
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function irodaUrlapHibak(a: IrodaUrlapAdat, most: Date = new Date()): Partial<Record<keyof IrodaUrlapAdat, string>> {
  const h: Partial<Record<keyof IrodaUrlapAdat, string>> = {};
  const slug = a.slug.trim();
  if (slug === '' || slug.length > 60 || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) h.slug = 'Csak kisbetű, szám és kötőjel (pl. duna-house-xiii).';
  else if (FOGLALT_SLUGOK.includes(slug)) h.slug = 'Ez a cím a weboldal egy saját oldaláé, válasszon másikat.';
  const nev = a.nev.trim();
  if (nev === '') h.nev = 'A név kötelező.';
  else if (nev.length > 120) h.nev = 'Legfeljebb 120 karakter.';
  if (a.hivasTelefon.trim() !== '' && !/^\+36[0-9]{8,9}$/.test(szokoz(a.hivasTelefon))) h.hivasTelefon = 'Nemzetközi formátumban add meg: +36…';
  if (!Number.isInteger(a.slaPerc) || a.slaPerc < 15 || a.slaPerc > 1440) h.slaPerc = '15 és 1440 perc között.';
  const logo = a.logoUrl.trim();
  if (logo !== '' && !/^https:\/\/\S+$/.test(logo)) h.logoUrl = 'Csak https:// címet adhatsz meg.';
  const email = a.kapcsolatEmail.trim();
  if (email !== '' && !EMAIL.test(email)) h.kapcsolatEmail = 'Ez nem érvényes e-mail-cím.';
  if (a.minosegElfogadva !== '') {
    // A szabályos naptári nap ellenőrzése: a budapestiNapEleje érvénytelen napra hibát dob.
    let ervenyesNap = /^\d{4}-\d{2}-\d{2}$/.test(a.minosegElfogadva);
    if (ervenyesNap) { try { budapestiNapEleje(a.minosegElfogadva); } catch { ervenyesNap = false; } }
    if (!ervenyesNap) h.minosegElfogadva = 'Érvénytelen dátum.';
    else if (a.minosegElfogadva > budapestiMaiNap(most)) h.minosegElfogadva = 'Az elfogadás napja nem lehet jövőbeli.';
  }
  if (a.kiosztasMod === 'vezetohoz' && !a.kiosztasVezetoId) h.kiosztasVezetoId = 'Válaszd ki az irodavezetőt.';
  return h;
}

const ures = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/**
 * Az elfogadás napjából időpont: a budapesti nap kezdete. Ha az eredeti időpont ugyanerre a napra esik, megmarad az eredeti
 * (különben egy másik mező mentése a napon belüli időpontot éjfélre csonkítaná).
 */
function minosegIdopont(nap: string, eredeti: string | null): string | null {
  if (nap === '') return null;
  if (eredeti && budapestiMaiNap(new Date(eredeti)) === nap) return eredeti;
  return budapestiNapEleje(nap);
}

export function irodaSorUrlapbol(a: IrodaUrlapAdat, eredetiMinosegElfogadva: string | null = null): OfficesInsert {
  const kapcsolat: Record<string, string> = {};
  const email = ures(a.kapcsolatEmail);
  const tel = ures(a.kapcsolatTelefon);
  if (email) kapcsolat.email = email;
  if (tel) kapcsolat.telefon = tel;
  const hivas = szokoz(a.hivasTelefon);
  return {
    slug: a.slug.trim(),
    nev: a.nev.trim(),
    halozat: ures(a.halozat),
    halozat_kulcs: a.halozatKulcs,
    aktiv: a.aktiv,
    regio: ures(a.regio),
    varos: ures(a.varos),
    logo_url: ures(a.logoUrl),
    kapcsolat,
    hivas_telefon: hivas === '' ? null : hivas,
    munkaido: munkaidoTisztitas(a.munkaido),
    sla_perc: a.slaPerc,
    minoseg_elfogadva_at: minosegIdopont(a.minosegElfogadva, eredetiMinosegElfogadva),
    kiosztas_mod: a.kiosztasMod,
    kiosztas_vezeto_id: a.kiosztasVezetoId,
  };
}

/** A „Hirdetési link” blokk Facebook-sora; érvénytelen vagy üres slugnál null (a blokk ilyenkor tájékoztatót mutat). */
export function facebookLink(alapUrl: string, slug: string): string | null {
  const s = slug.trim();
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(s)) return null;
  return hirdetesiLink(alapUrl, s, 'facebook');
}
